import React, { useEffect, useState } from "react";
import { Layout, PageHeader } from "@/components/Layout";
import { KeyRound, Building2, UserCog, MessageCircle, FileDown, Layers, Pencil, Trash2, Check, X, Percent, DatabaseZap, RefreshCw, Calculator } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { useLang } from "@/context/LanguageContext";

const ADMIN_ROLES = ["director", "manager", "admin"];
const SUPERADMIN_ROLES = ["manager", "admin"];
const TONE_LABELS = { overdue: "Overdue (firm)", due_today: "Due Today", due_tomorrow: "Due Tomorrow (gentle)", default: "Default / Upcoming" };
const inputCls =
  "w-full bg-white border border-slate-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-slate-900 outline-none";
const labelCls = "text-xs font-semibold tracking-wide uppercase text-slate-500";
const cardCls = "bg-white border border-slate-200 rounded-lg p-6";

export default function Account() {
  const { t } = useLang();
  const { user } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  const isSuperAdmin = SUPERADMIN_ROLES.includes(user?.role);
  const [tab, setTab] = useState("my");

  const tabs = [
    { id: "my", label: "My Account" },
    ...(isAdmin ? [
      { id: "branches", label: "Branches" },
      { id: "commission", label: "Commission" },
      { id: "team", label: "Team & Access" },
    ] : []),
    ...(isSuperAdmin ? [{ id: "maintenance", label: "Maintenance" }] : []),
    { id: "reminders", label: "Reminders" },
  ];

  const gridCls = "grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl";

  return (
    <Layout>
      <PageHeader title={t("acct.settings")} subtitle="Manage your password, branches and team logins" />

      <div className="flex flex-wrap items-center gap-1 border-b border-slate-200 mb-6 max-w-5xl" data-testid="account-tabs">
        {tabs.map((tb) => (
          <button
            key={tb.id}
            data-testid={`account-tab-${tb.id}`}
            onClick={() => setTab(tb.id)}
            className={`px-4 py-2.5 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === tb.id
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            {tb.label}
          </button>
        ))}
      </div>

      <div data-testid={`account-panel-${tab}`}>
        {tab === "my" && (
          <div className={gridCls}>
            <ChangePasswordCard />
            <ManualDownloadCard />
          </div>
        )}
        {tab === "branches" && isAdmin && (
          <div className={gridCls}>
            <CreateBranchCard />
            <div className="lg:col-span-2"><ManageBranchesCard /></div>
          </div>
        )}
        {tab === "commission" && isAdmin && (
          <div className={gridCls}>
            <div className="lg:col-span-2"><CommissionSettingsCard /></div>
          </div>
        )}
        {tab === "team" && isAdmin && (
          <div className={gridCls}>
            <ResetPasswordCard />
            <div className="lg:col-span-2"><DirectorScopeCard /></div>
          </div>
        )}
        {tab === "maintenance" && isSuperAdmin && (
          <div className={gridCls}>
            <div className="lg:col-span-2"><LedgerFixCard /></div>
            <div className="lg:col-span-2"><RecalcLoansCard /></div>
            <div className="lg:col-span-2"><RestoreLedgerCard /></div>
            <div className="lg:col-span-2"><MissingLoansCard /></div>
          </div>
        )}
        {tab === "reminders" && (
          <div className={gridCls}>
            <div className="lg:col-span-2"><ReminderTemplatesCard /></div>
          </div>
        )}
      </div>
    </Layout>
  );
}

function fmt(n) {
  if (n === null || n === undefined) return "—";
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function RecalcLoansCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const pollRef = React.useRef(null);

  const poll = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/recalculate-loans/status");
      setStatus(data);
      if (data.state !== "running") {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
        if (data.state === "done") toast.success(data.report?.mode === "commit" ? "贷款已重算" : "预览完成");
        else if (data.state === "error") toast.error(`出错: ${data.message}`);
      }
    } catch (e) {
      clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);

  const run = async (commit) => {
    setConfirming(false); setBusy(true);
    setStatus({ state: "running", message: "starting…", report: null });
    try {
      await api.post("/admin/recalculate-loans/start", null, { params: { commit } });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(poll, 2000);
      poll();
    } catch (e) { setBusy(false); toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const report = status?.report;
  const rows = report?.branches || [];

  return (
    <div className={cardCls} data-testid="recalc-loans-card">
      <div className="flex items-center gap-2 mb-2">
        <Calculator className="w-5 h-5 text-slate-700" />
        <h3 className="text-base font-semibold text-slate-900">重算现有贷款 / Recalculate Existing Loans</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4 leading-relaxed">
        把新公式套用到<b>所有已存在的历史贷款</b>,一次性修正每笔的 In Hand、Money Used、Actual Profit/Loss:
        <br />• In Hand = 借款额 − 印花　• Money Used = In Hand + 佣金　• Actual P/L = 已收总额 − Money Used
        <br />
        <b>只改贷款字段,不动账本现金余额、不动 Outstanding、不动贷款状态。</b>
        用新公式建的贷款差额为 0;旧/导入的贷款会按印花、佣金产生差额。执行前会自动把整份贷款备份到
        <code>loans_backup_recalc</code>。先点「预览」核对逐分行差额。
      </p>

      <div className="flex flex-wrap gap-3 mb-4">
        <button data-testid="recalc-loans-dryrun-btn" disabled={busy} onClick={() => run(false)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-slate-300 text-slate-800 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> 预览 / Dry Run
        </button>
        <button data-testid="recalc-loans-run-btn" disabled={busy} onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          <Calculator className="w-4 h-4" /> 执行重算 (写入) / Recalculate
        </button>
      </div>

      {confirming && (
        <div className="mb-4 p-4 rounded-md border border-amber-300 bg-amber-50" data-testid="recalc-loans-confirm">
          <p className="text-sm text-amber-900 mb-3">确认要用新公式重算并写回所有贷款吗?写入前会自动备份贷款集合。账本现金余额不受影响。</p>
          <div className="flex gap-2">
            <button data-testid="recalc-loans-confirm-yes" onClick={() => run(true)}
              className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700">确认重算</button>
            <button data-testid="recalc-loans-confirm-no" onClick={() => setConfirming(false)}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-white">取消</button>
          </div>
        </div>
      )}

      {status && (
        <div className="text-sm">
          <div className="mb-2 text-slate-600" data-testid="recalc-loans-state">
            状态: <span className="font-semibold">{status.state}</span>
            {status.state === "running" && <span> — {status.message}…</span>}
            {report && (
              <span className="ml-2">
                模式: <span className="font-semibold">{report.mode === "commit" ? "已写入" : "预览"}</span>
                {" · "}将变动: <span className="font-semibold">{report.totalChanged}</span>/{report.totalLoans} 笔
                {" · "}利润总变动: <span className={`font-semibold ${report.totalProfitDelta < 0 ? "text-red-600" : "text-emerald-600"}`}>{fmt(report.totalProfitDelta)}</span>
              </span>
            )}
          </div>
          {rows.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm" data-testid="recalc-loans-table">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">分行</th>
                    <th className="text-right px-3 py-2 font-medium">变动笔数</th>
                    <th className="text-right px-3 py-2 font-medium">利润变动</th>
                    <th className="text-left px-3 py-2 font-medium">样例(编号 旧→新 P/L)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.branch} className="border-t border-slate-100 align-top" data-testid={`recalc-loans-row-${r.branch}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.branch}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{r.changed}</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${r.plDelta < 0 ? "text-red-600" : "text-emerald-600"}`}>{fmt(r.plDelta)}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">
                        {(r.samples || []).map((s, i) => (
                          <div key={i}>{s.code}: P/L {fmt(s.oldPL)}→{fmt(s.newPL)} · MU {fmt(s.oldMoneyUsed)}→{fmt(s.newMoneyUsed)}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function RestoreLedgerCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const pollRef = React.useRef(null);

  const poll = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/restore-colleague-ledger/status");
      setStatus(data);
      if (data.state !== "running") {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
        if (data.state === "done") toast.success(data.report?.mode === "commit" ? "同事账本已恢复" : "预览完成");
        else if (data.state === "error") toast.error(`出错: ${data.message}`);
      }
    } catch (e) {
      clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);

  const run = async (commit) => {
    setConfirming(false); setBusy(true);
    setStatus({ state: "running", message: "starting…", report: null });
    try {
      await api.post("/admin/restore-colleague-ledger/start", null, { params: { commit } });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(poll, 2000);
      poll();
    } catch (e) { setBusy(false); toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const report = status?.report;
  const rows = report?.branches || [];

  return (
    <div className={cardCls} data-testid="restore-ledger-card">
      <div className="flex items-center gap-2 mb-2">
        <RefreshCw className="w-5 h-5 text-slate-700" />
        <h3 className="text-base font-semibold text-slate-900">恢复同事新增账本 / Restore Colleague Ledger</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4 leading-relaxed">
        「修复历史账本」会用 Excel 的历史数据覆盖账本。如果修复前同事已在系统里新建过贷款/付款,
        那些自动生成的账本流水会被覆盖 —— 但已<b>备份</b>在 <code>ledger_backup_prodfix</code>。
        此功能会从备份里挑出<b>非导入</b>(同事/手动新增)、且当前账本里缺失的流水,重新加回,
        <b>不会</b>重新引入旧的错误历史数据。先点「预览」看看是哪些。
      </p>

      <div className="flex flex-wrap gap-3 mb-4">
        <button data-testid="restore-ledger-dryrun-btn" disabled={busy} onClick={() => run(false)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-slate-300 text-slate-800 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> 预览 / Dry Run
        </button>
        <button data-testid="restore-ledger-run-btn" disabled={busy} onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          <RefreshCw className="w-4 h-4" /> 恢复 (写入) / Restore
        </button>
      </div>

      {confirming && (
        <div className="mb-4 p-4 rounded-md border border-amber-300 bg-amber-50" data-testid="restore-ledger-confirm">
          <p className="text-sm text-amber-900 mb-3">确认要把这些同事新增的账本流水加回吗?只会插入当前缺失的记录。</p>
          <div className="flex gap-2">
            <button data-testid="restore-ledger-confirm-yes" onClick={() => run(true)}
              className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700">确认恢复</button>
            <button data-testid="restore-ledger-confirm-no" onClick={() => setConfirming(false)}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-white">取消</button>
          </div>
        </div>
      )}

      {status && (
        <div className="text-sm">
          <div className="mb-2 text-slate-600" data-testid="restore-ledger-state">
            状态: <span className="font-semibold">{status.state}</span>
            {status.state === "running" && <span> — {status.message}…</span>}
            {report && !report.error && (
              <span className="ml-2">模式: <span className="font-semibold">{report.mode === "commit" ? "已写入" : "预览"}</span>
                {" · "}将恢复: <span className="font-semibold">{report.toRestore}</span> 条</span>
            )}
            {report?.error && <span className="ml-2 text-red-600">{report.error}</span>}
          </div>
          {rows.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm" data-testid="restore-ledger-table">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">分行</th>
                    <th className="text-right px-3 py-2 font-medium">条数</th>
                    <th className="text-left px-3 py-2 font-medium">示例(日期/类型/金额/说明)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.branch} className="border-t border-slate-100 align-top" data-testid={`restore-ledger-row-${r.branch}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.branch}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{r.count}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">
                        {(r.samples || []).map((s, i) => (
                          <div key={i}>{s.date} · {s.type} · {fmt(s.amount)} · {s.desc}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function MissingLoansCard() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const pollRef = React.useRef(null);

  const poll = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/add-missing-loans/status");
      setStatus(data);
      if (data.state !== "running") {
        clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
        if (data.state === "done") toast.success(data.report?.mode === "commit" ? "缺失贷款已补全" : "预览完成");
        else if (data.state === "error") toast.error(`出错: ${data.message}`);
      }
    } catch (e) {
      clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);

  const run = async (commit) => {
    setConfirming(false); setBusy(true);
    setStatus({ state: "running", message: "starting…", report: null });
    try {
      await api.post("/admin/add-missing-loans/start", null, { params: { commit } });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(poll, 2000);
      poll();
    } catch (e) { setBusy(false); toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const report = status?.report;
  const rows = report?.branches || [];

  return (
    <div className={cardCls} data-testid="missing-loans-card">
      <div className="flex items-center gap-2 mb-2">
        <Layers className="w-5 h-5 text-slate-700" />
        <h3 className="text-base font-semibold text-slate-900">补全缺失贷款 / Add Missing Loans</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4 leading-relaxed">
        有些较新的贷款出现在账本(现金流)里,但贷款登记册还没有(登记册导入较早)。此功能会从最新 Excel
        <b>只新增</b>这些缺失的贷款,<b>不会修改或删除</b>任何现有贷款(催收阶段、备注、联系资料等全部保留)。
        RAY 因编码格式特殊未包含,需单独处理。
      </p>

      <div className="flex flex-wrap gap-3 mb-4">
        <button data-testid="missing-loans-dryrun-btn" disabled={busy} onClick={() => run(false)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-slate-300 text-slate-800 hover:bg-slate-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> 预览 / Dry Run
        </button>
        <button data-testid="missing-loans-run-btn" disabled={busy} onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          <Layers className="w-4 h-4" /> 补全 (写入) / Add
        </button>
      </div>

      {confirming && (
        <div className="mb-4 p-4 rounded-md border border-amber-300 bg-amber-50" data-testid="missing-loans-confirm">
          <p className="text-sm text-amber-900 mb-3">确认要新增这些贷款吗?只会插入不存在的记录,现有贷款不受影响。</p>
          <div className="flex gap-2">
            <button data-testid="missing-loans-confirm-yes" onClick={() => run(true)}
              className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700">确认新增</button>
            <button data-testid="missing-loans-confirm-no" onClick={() => setConfirming(false)}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-white">取消</button>
          </div>
        </div>
      )}

      {status && (
        <div className="text-sm">
          <div className="mb-2 text-slate-600" data-testid="missing-loans-state">
            状态: <span className="font-semibold">{status.state}</span>
            {status.state === "running" && <span> — {status.message}…</span>}
            {report && (
              <span className="ml-2">
                模式: <span className="font-semibold">{report.mode === "commit" ? "已写入" : "预览"}</span>
                {" · "}将新增: <span className="font-semibold">{report.totalToAdd}</span>
                {" · "}已存在跳过: <span className="font-semibold">{report.alreadyPresent}</span>
              </span>
            )}
          </div>
          {rows.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm" data-testid="missing-loans-table">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">分行</th>
                    <th className="text-right px-3 py-2 font-medium">新增</th>
                    <th className="text-left px-3 py-2 font-medium">示例代码</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.filter((r) => r.add > 0).map((r) => (
                    <tr key={r.branch} className="border-t border-slate-100" data-testid={`missing-loans-row-${r.branch}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.branch}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{r.add}</td>
                      <td className="px-3 py-2 text-slate-500 text-xs">{(r.samples || []).join(", ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LedgerFixCard() {
  const [status, setStatus] = useState(null);   // { state, message, report }
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const pollRef = React.useRef(null);

  const poll = React.useCallback(async () => {
    try {
      const { data } = await api.get("/admin/rebuild-ledgers/status");
      setStatus(data);
      if (data.state !== "running") {
        clearInterval(pollRef.current);
        pollRef.current = null;
        setBusy(false);
        if (data.state === "done") {
          const mode = data.report?.mode === "commit" ? "修复已完成" : "预览完成";
          toast.success(mode);
        } else if (data.state === "error") {
          toast.error(`执行出错: ${data.message}`);
        }
      }
    } catch (e) {
      clearInterval(pollRef.current); pollRef.current = null; setBusy(false);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  }, []);

  const run = async (commit) => {
    setConfirming(false);
    setBusy(true);
    setStatus({ state: "running", message: "starting…", report: null });
    try {
      await api.post("/admin/rebuild-ledgers/start", null, { params: { commit } });
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(poll, 2000);
      poll();
    } catch (e) {
      setBusy(false);
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const report = status?.report;
  const rows = report?.branches || [];
  const changedCount = rows.filter((r) => r.changed).length;

  return (
    <div className={cardCls} data-testid="ledger-fix-card">
      <div className="flex items-center gap-2 mb-2">
        <DatabaseZap className="w-5 h-5 text-slate-700" />
        <h3 className="text-base font-semibold text-slate-900">修复历史账本 / Ledger Fix</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4 leading-relaxed">
        把各分行的账本(现金余额)重建到与 Excel 完全一致,并清除错误的「未来日期」交易。
        先点「预览」核对新旧余额,确认无误后再点「执行修复」。执行前会自动备份,可回滚。
        只影响账本数据,不动用户、密码和其它记录。
      </p>

      <div className="flex flex-wrap gap-3 mb-4">
        <button
          data-testid="ledger-fix-dryrun-btn"
          disabled={busy}
          onClick={() => run(false)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border border-slate-300 text-slate-800 hover:bg-slate-50 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${busy && status?.report?.mode !== "commit" ? "animate-spin" : ""}`} />
          预览 (不写入) / Dry Run
        </button>
        <button
          data-testid="ledger-fix-run-btn"
          disabled={busy}
          onClick={() => setConfirming(true)}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
        >
          <DatabaseZap className="w-4 h-4" />
          执行修复 (写入) / Run Fix
        </button>
      </div>

      {confirming && (
        <div className="mb-4 p-4 rounded-md border border-amber-300 bg-amber-50" data-testid="ledger-fix-confirm">
          <p className="text-sm text-amber-900 mb-3">
            确认要把账本写入数据库吗?执行前会自动备份到 <code>ledger_backup_prodfix</code>。
          </p>
          <div className="flex gap-2">
            <button data-testid="ledger-fix-confirm-yes" onClick={() => run(true)}
              className="px-3 py-1.5 text-sm rounded-md bg-amber-600 text-white hover:bg-amber-700">
              确认执行
            </button>
            <button data-testid="ledger-fix-confirm-no" onClick={() => setConfirming(false)}
              className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-white">
              取消
            </button>
          </div>
        </div>
      )}

      {status && (
        <div className="text-sm">
          <div className="mb-2 text-slate-600" data-testid="ledger-fix-state">
            状态: <span className="font-semibold">{status.state}</span>
            {status.state === "running" && <span> — {status.message}…</span>}
            {report && (
              <span className="ml-2">
                模式: <span className="font-semibold">{report.mode === "commit" ? "已写入" : "预览"}</span>
                {" · "}需修改的分行: <span className="font-semibold">{changedCount}</span>
                {report.totalFutureAfter !== null && report.totalFutureAfter !== undefined && (
                  <> · 修复后未来日期条目: <span className="font-semibold">{report.totalFutureAfter}</span></>
                )}
              </span>
            )}
          </div>

          {rows.length > 0 && (
            <div className="overflow-x-auto border border-slate-200 rounded-md">
              <table className="w-full text-sm" data-testid="ledger-fix-table">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium">分行</th>
                    <th className="text-right px-3 py-2 font-medium">当前余额</th>
                    <th className="text-right px-3 py-2 font-medium">修正后余额</th>
                    <th className="text-right px-3 py-2 font-medium">当前未来条目</th>
                    <th className="text-center px-3 py-2 font-medium">变化</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.branch} className={`border-t border-slate-100 ${r.changed ? "bg-amber-50/40" : ""}`}
                        data-testid={`ledger-fix-row-${r.branch}`}>
                      <td className="px-3 py-2 font-medium text-slate-800">{r.branch}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-600">{fmt(r.currentBalance)}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-slate-900">{fmt(r.newBalance)}</td>
                      <td className={`px-3 py-2 text-right tabular-nums ${r.currentFuture > 0 ? "text-red-600 font-semibold" : "text-slate-400"}`}>{r.currentFuture}</td>
                      <td className="px-3 py-2 text-center">
                        {r.changed
                          ? <span className="inline-block px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-medium">需修正</span>
                          : <span className="inline-block px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-medium">已一致</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function manualFileFor(user) {
  const role = user?.role;
  const email = (user?.email || "").split("@")[0].toLowerCase();
  if (role === "branch_manager" && user?.branch) return `branch_manager_${user.branch}.pdf`;
  if (role === "assistant") return "pa_manual.pdf";
  if (role === "clerk") return "clerk_manual.pdf";
  if (role === "director") {
    if (email.includes("eric")) return "director_eric.pdf";
    if (email.includes("dennis")) return "director_dennis.pdf";
  }
  return null; // admin / manager -> full bundle only
}

function ManualDownloadCard() {
  const { user } = useAuth();
  const { t } = useLang();
  const file = manualFileFor(user);
  const isAdmin = ADMIN_ROLES.includes(user?.role);
  return (
    <div className={cardCls} data-testid="manual-download-card">
      <div className="flex items-center gap-2 mb-4">
        <FileDown className="h-5 w-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">{t("acct.manual_title")}</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">{t("acct.manual_desc")}</p>
      <div className="flex flex-wrap gap-3">
        {file && (
          <a
            data-testid="download-my-manual"
            href={`/manuals/${file}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-700"
          >
            <FileDown className="h-4 w-4" /> {t("acct.manual_download")}
          </a>
        )}
        {(isAdmin || !file) && (
          <a
            data-testid="download-all-manuals"
            href="/manuals/diamond_manuals.zip"
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium ${file ? "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50" : "bg-slate-900 text-white hover:bg-slate-700"}`}
          >
            <FileDown className="h-4 w-4" /> {t("acct.manual_all")}
          </a>
        )}
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ currentPassword: "", newPassword: "", confirm: "" });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.newPassword.length < 6) return toast.error("New password must be at least 6 characters.");
    if (form.newPassword !== form.confirm) return toast.error("New passwords do not match.");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      toast.success("Password changed successfully.");
      setForm({ currentPassword: "", newPassword: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cardCls} data-testid="change-password-card">
      <div className="flex items-center gap-2 mb-4">
        <KeyRound className="h-5 w-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">Change My Password</h3>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Current Password</label>
          <input type="password" data-testid="current-password-input" className={`${inputCls} mt-1.5`}
            value={form.currentPassword} onChange={(e) => setForm({ ...form, currentPassword: e.target.value })} required />
        </div>
        <div>
          <label className={labelCls}>New Password</label>
          <input type="password" data-testid="new-password-input" className={`${inputCls} mt-1.5`}
            value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} required />
        </div>
        <div>
          <label className={labelCls}>Confirm New Password</label>
          <input type="password" data-testid="confirm-password-input" className={`${inputCls} mt-1.5`}
            value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required />
        </div>
        <button type="submit" disabled={busy} data-testid="change-password-submit"
          className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
          {busy ? "Saving…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}

function CreateBranchCard() {
  const [form, setForm] = useState({ code: "", name: "", prefix: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setCreated(null);
    try {
      const { data } = await api.post("/branches", {
        code: form.code, name: form.name,
        prefix: form.prefix || undefined,
        password: form.password || undefined,
      });
      toast.success(`Branch ${data.branch.code} created.`);
      setCreated(data.account);
      setForm({ code: "", name: "", prefix: "", password: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to create branch");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cardCls} data-testid="create-branch-card">
      <div className="flex items-center gap-2 mb-4">
        <Building2 className="h-5 w-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">Create New Branch</h3>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Branch Code</label>
          <input data-testid="branch-code-input" className={`${inputCls} mt-1.5 uppercase`} placeholder="e.g. PJ"
            value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} required />
          <p className="text-xs text-slate-400 mt-1">Login account will be <span className="font-mono-fig">{(form.code || "code").toLowerCase()}@diamond.com</span></p>
        </div>
        <div>
          <label className={labelCls}>Branch Name</label>
          <input data-testid="branch-name-input" className={`${inputCls} mt-1.5`} placeholder="e.g. Petaling Jaya Branch"
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Loan Prefix (optional)</label>
            <input data-testid="branch-prefix-input" className={`${inputCls} mt-1.5 uppercase`} placeholder="defaults to code"
              value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value.toUpperCase() })} />
          </div>
          <div>
            <label className={labelCls}>Default Password (optional)</label>
            <input data-testid="branch-password-input" className={`${inputCls} mt-1.5`} placeholder="defaults to code123"
              value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          </div>
        </div>
        <button type="submit" disabled={busy} data-testid="create-branch-submit"
          className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
          {busy ? "Creating…" : "Create Branch"}
        </button>
      </form>
      {created && (
        <div data-testid="branch-created-banner" className="mt-4 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800">
          New login created — <span className="font-mono-fig font-semibold">{created.email}</span> /{" "}
          <span className="font-mono-fig font-semibold">{created.defaultPassword}</span>. Ask the branch to change it after first sign-in.
        </div>
      )}
    </div>
  );
}

function ManageBranchesCard() {
  const [branches, setBranches] = useState([]);
  const [editing, setEditing] = useState({});
  const [confirming, setConfirming] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/branches").then(({ data }) => setBranches(data.branches || [])).catch(() => {});
  };
  useEffect(load, []);

  const saveName = async (code) => {
    const name = (editing[code] || "").trim();
    if (!name) { toast.error("Branch name cannot be empty"); return; }
    setBusy(true);
    try {
      await api.put(`/branches/${code}`, { name });
      toast.success(`Branch ${code} renamed.`);
      setEditing((e) => { const n = { ...e }; delete n[code]; return n; });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to rename branch");
    } finally { setBusy(false); }
  };

  const doDelete = async (code) => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/branches/${code}`);
      toast.success(`Branch ${code} deleted${data.deletedAccount ? ` (login ${data.deletedAccount} removed)` : ""}.`);
      setConfirming(null); setConfirmText("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to delete branch");
    } finally { setBusy(false); }
  };

  return (
    <div className={cardCls} data-testid="manage-branches-card">
      <div className="flex items-center gap-2 mb-4">
        <Layers className="h-5 w-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">Manage Branches</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Rename a branch or delete an empty one. A branch can only be deleted when it has no loans and no ledger records; deleting also removes its login account.
      </p>
      <div className="space-y-2">
        {branches.length === 0 && <p className="text-sm text-slate-400">No branches yet.</p>}
        {branches.map((b) => {
          const isEditing = editing[b.code] !== undefined;
          const isConfirming = confirming === b.code;
          return (
            <div key={b.code} className="border border-slate-200 rounded-md p-3" data-testid={`branch-row-${b.code}`}>
              <div className="flex items-center gap-3">
                <span className="font-mono-fig text-xs font-semibold bg-slate-100 text-slate-700 rounded px-2 py-1">{b.code}</span>
                {isEditing ? (
                  <input data-testid={`branch-edit-input-${b.code}`} className={`${inputCls} flex-1`} value={editing[b.code]}
                    onChange={(e) => setEditing((s) => ({ ...s, [b.code]: e.target.value }))} />
                ) : (
                  <span className="flex-1 text-sm text-slate-800">{b.name}</span>
                )}
                {isEditing ? (
                  <>
                    <button data-testid={`branch-save-${b.code}`} disabled={busy} onClick={() => saveName(b.code)}
                      className="text-emerald-600 hover:text-emerald-800 p-1" title="Save"><Check className="h-4 w-4" /></button>
                    <button data-testid={`branch-cancel-edit-${b.code}`} onClick={() => setEditing((e) => { const n = { ...e }; delete n[b.code]; return n; })}
                      className="text-slate-400 hover:text-slate-600 p-1" title="Cancel"><X className="h-4 w-4" /></button>
                  </>
                ) : (
                  <>
                    <button data-testid={`branch-edit-${b.code}`} onClick={() => setEditing((s) => ({ ...s, [b.code]: b.name }))}
                      className="text-slate-500 hover:text-slate-900 p-1" title="Rename"><Pencil className="h-4 w-4" /></button>
                    <button data-testid={`branch-delete-${b.code}`} onClick={() => { setConfirming(b.code); setConfirmText(""); }}
                      className="text-red-500 hover:text-red-700 p-1" title="Delete"><Trash2 className="h-4 w-4" /></button>
                  </>
                )}
              </div>
              {isConfirming && (
                <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3" data-testid={`branch-delete-confirm-${b.code}`}>
                  <p className="text-sm text-red-800 mb-2">
                    Permanently delete branch <span className="font-semibold">{b.code}</span> and its login account. Type{" "}
                    <span className="font-mono-fig font-semibold">{b.code}</span> to confirm.
                  </p>
                  <div className="flex items-center gap-2">
                    <input data-testid={`branch-delete-confirm-input-${b.code}`} className={`${inputCls} flex-1 uppercase`} value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value.toUpperCase())} placeholder={b.code} />
                    <button data-testid={`branch-delete-confirm-btn-${b.code}`} disabled={busy || confirmText !== b.code} onClick={() => doDelete(b.code)}
                      className="bg-red-600 text-white rounded-md px-3 py-2 text-sm font-medium hover:bg-red-700 disabled:opacity-40">Delete</button>
                    <button data-testid={`branch-delete-cancel-${b.code}`} onClick={() => { setConfirming(null); setConfirmText(""); }}
                      className="border border-slate-300 rounded-md px-3 py-2 text-sm">Cancel</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function CommissionSettingsCard() {
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/commission/branches").then(({ data }) => {
      setBranches(data.branches || []);
      setUsers(data.users || []);
      const d = {};
      (data.branches || []).forEach((b) => {
        d[b.code] = {
          commissionEnabled: b.commissionEnabled,
          commissionRate: b.commissionRate,
          commissionManagerId: b.commissionManagerId || "",
        };
      });
      setDrafts(d);
    }).catch(() => {});
  };
  useEffect(load, []);

  const setField = (code, key, val) => setDrafts((s) => ({ ...s, [code]: { ...s[code], [key]: val } }));

  const save = async (code) => {
    const d = drafts[code];
    setBusy(code);
    try {
      await api.put(`/commission/branches/${code}`, {
        commissionEnabled: !!d.commissionEnabled,
        commissionRate: Number(d.commissionRate) || 0,
        commissionManagerId: d.commissionManagerId || null,
      });
      toast.success(`Commission settings saved for ${code}.`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally { setBusy(""); }
  };

  return (
    <div className={cardCls} data-testid="commission-settings-card">
      <div className="flex items-center gap-2 mb-4">
        <Percent className="h-5 w-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">Commission Settings</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Turn on the Branch Manager Commission module per branch, set the commission rate, and choose the earning manager. Only enabled branches appear in the Commission report and month-end job.
      </p>
      <div className="space-y-2">
        {branches.length === 0 && <p className="text-sm text-slate-400">No branches.</p>}
        {branches.map((b) => {
          const d = drafts[b.code] || {};
          return (
            <div key={b.code} className="border border-slate-200 rounded-md p-3 flex flex-wrap items-center gap-3" data-testid={`commission-setting-${b.code}`}>
              <span className="font-mono-fig text-xs font-semibold bg-slate-100 text-slate-700 rounded px-2 py-1">{b.code}</span>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" data-testid={`commission-enabled-${b.code}`} checked={!!d.commissionEnabled}
                  onChange={(e) => setField(b.code, "commissionEnabled", e.target.checked)} className="h-4 w-4" />
                Enabled
              </label>
              <div className="inline-flex items-center gap-1">
                <input type="number" min="0" max="100" step="0.1" data-testid={`commission-rate-${b.code}`}
                  value={d.commissionRate ?? 0} onChange={(e) => setField(b.code, "commissionRate", e.target.value)}
                  className="w-20 bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm" />
                <span className="text-sm text-slate-500">%</span>
              </div>
              <select data-testid={`commission-manager-${b.code}`} value={d.commissionManagerId || ""}
                onChange={(e) => setField(b.code, "commissionManagerId", e.target.value)}
                className="flex-1 min-w-[180px] bg-white border border-slate-300 rounded-md px-2 py-1.5 text-sm">
                <option value="">— select manager —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.role})</option>)}
              </select>
              <button data-testid={`commission-save-${b.code}`} onClick={() => save(b.code)} disabled={busy === b.code}
                className="bg-slate-900 text-white rounded-md px-3 py-1.5 text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
                {busy === b.code ? "Saving…" : "Save"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function ResetPasswordCard() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ email: "", newPassword: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/admin/users").then((r) => setUsers(Array.isArray(r.data) ? r.data : (r.data?.users || []))).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) return toast.error("Select an account.");
    if (form.newPassword.length < 6) return toast.error("Password must be at least 6 characters.");
    setBusy(true);
    try {
      await api.post("/admin/reset-password", form);
      toast.success(`Password reset for ${form.email}.`);
      setForm({ email: "", newPassword: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={cardCls} data-testid="reset-password-card">
      <div className="flex items-center gap-2 mb-4">
        <UserCog className="h-5 w-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">Reset a Team Login</h3>
      </div>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className={labelCls}>Account</label>
          <select data-testid="reset-user-select" className={`${inputCls} mt-1.5`}
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}>
            <option value="">Select account…</option>
            {users.map((u) => (
              <option key={u.email} value={u.email}>{u.email} ({u.role}{u.branch ? ` · ${u.branch}` : ""})</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>New Password</label>
          <input type="password" data-testid="reset-new-password-input" className={`${inputCls} mt-1.5`}
            value={form.newPassword} onChange={(e) => setForm({ ...form, newPassword: e.target.value })} required />
        </div>
        <button type="submit" disabled={busy} data-testid="reset-password-submit"
          className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
          {busy ? "Resetting…" : "Reset Password"}
        </button>
      </form>
    </div>
  );
}

function DirectorScopeCard() {
  const [directors, setDirectors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/directors");
      setDirectors(data.directors || []);
      setBranches(data.branches || []);
      const map = {};
      (data.directors || []).forEach((d) => { map[d.id] = d.scopeBranches || []; });
      setSelected(map);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load directors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (dId, code) => {
    setSelected((prev) => {
      const cur = new Set(prev[dId] || []);
      if (cur.has(code)) cur.delete(code); else cur.add(code);
      return { ...prev, [dId]: Array.from(cur) };
    });
  };

  const save = async (d) => {
    setBusy(d.id);
    try {
      await api.put(`/admin/users/${d.id}/scope`, { scopeBranches: selected[d.id] || [] });
      toast.success(`Branches updated for ${d.name || d.email}.`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to update branches");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className={cardCls} data-testid="director-scope-card">
      <div className="flex items-center gap-2 mb-1">
        <Layers className="h-5 w-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">Director Branch Assignments</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Assign or transfer branches to each director. Tick the branches a director should oversee, then Save. The director must sign in again for changes to take effect.
      </p>
      {loading ? (
        <div className="text-sm text-slate-400 py-6">Loading directors…</div>
      ) : directors.length === 0 ? (
        <div className="text-sm text-slate-400 py-6">No director accounts found.</div>
      ) : (
        <div className="space-y-6">
          {directors.map((d) => {
            const chosen = new Set(selected[d.id] || []);
            return (
              <div key={d.id} className="border border-slate-200 rounded-md p-4" data-testid={`director-block-${d.email}`}>
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <div className="font-semibold text-slate-900">{d.name || d.email}</div>
                    <div className="text-xs text-slate-400 font-mono-fig">{d.email} · {chosen.size} branches</div>
                  </div>
                  <button type="button" onClick={() => save(d)} disabled={busy === d.id}
                    data-testid={`director-scope-save-${d.email}`}
                    className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
                    {busy === d.id ? "Saving…" : "Save"}
                  </button>
                </div>
                <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
                  {branches.map((code) => {
                    const on = chosen.has(code);
                    return (
                      <button type="button" key={code} onClick={() => toggle(d.id, code)}
                        data-testid={`branch-chip-${d.email}-${code}`}
                        className={`text-xs font-mono-fig rounded-md px-2 py-1.5 border transition-colors ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-500"}`}>
                        {code}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function ReminderTemplatesCard() {
  const { branch } = useBranch();
  const [templates, setTemplates] = useState(null);
  const [placeholders, setPlaceholders] = useState([]);
  const [defaults, setDefaults] = useState({});
  const [activeBranch, setActiveBranch] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reminders/templates");
      setTemplates(data.templates || {});
      setPlaceholders(data.placeholders || []);
      setDefaults(data.defaults || {});
      setActiveBranch(data.branch || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load reminder messages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, branch]);

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/reminders/templates", { templates, branch: activeBranch || undefined });
      setTemplates(data.templates || templates);
      toast.success("Reminder messages saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save reminder messages");
    } finally {
      setBusy(false);
    }
  };

  const resetTone = (tone) => setTemplates((t) => ({ ...t, [tone]: defaults[tone] || "" }));

  return (
    <div className={cardCls} data-testid="reminder-templates-card">
      <div className="flex items-center gap-2 mb-1">
        <MessageCircle className="h-5 w-5 text-slate-700" />
        <h3 className="font-heading font-semibold text-slate-900">WhatsApp Reminder Messages</h3>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Customise the click-to-chat reminder wording for <span className="font-semibold">{activeBranch || branch}</span>. Leave blank to use the default.
      </p>
      {placeholders.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="reminder-placeholders">
          {placeholders.map((p) => (
            <span key={p} className="font-mono-fig text-xs bg-slate-100 text-slate-700 rounded px-2 py-1">{p}</span>
          ))}
        </div>
      )}
      {loading || !templates ? (
        <div className="text-sm text-slate-400 py-6">Loading reminder messages…</div>
      ) : (
        <div className="space-y-5">
          {Object.keys(TONE_LABELS).map((tone) => (
            <div key={tone}>
              <div className="flex items-center justify-between mb-1.5">
                <label className={labelCls}>{TONE_LABELS[tone]}</label>
                <button type="button" onClick={() => resetTone(tone)} data-testid={`reminder-reset-${tone}`}
                  className="text-xs text-slate-500 hover:text-slate-900 underline">Reset to default</button>
              </div>
              <textarea data-testid={`reminder-textarea-${tone}`} rows={3} className={inputCls}
                value={templates[tone] || ""} onChange={(e) => setTemplates({ ...templates, [tone]: e.target.value })} />
            </div>
          ))}
          <button type="button" onClick={save} disabled={busy} data-testid="reminder-save-button"
            className="bg-slate-900 text-white rounded-md px-4 py-2 text-sm font-medium hover:bg-slate-700 disabled:opacity-50">
            {busy ? "Saving…" : "Save Messages"}
          </button>
        </div>
      )}
    </div>
  );
}
